<template>
    <el-dialog
        :visible="visible"
        title="Users with Line Conflicts"
        width="95vw"
        top="10vh"
        append-to-body
        custom-class="users-with-line-conflicts-modal"
        @close="onClose"
    >
        <div class="break-word mt--1">            
            <div v-if="hasConflicts" class="mb-4">
                The Line "{{ companyDefaultOutboundLineName }}" is forced as Default Outbound Line at Company Level and some agents doesn't have access to the line and aren’t able to use it.
            </div>

            <el-card v-if="tableData.length" v-loading="loading" shadow="never" class="conflicts-card mb-3 rounded-card">
                <div class="font-weight-bold">
                    <span v-if="!isPreviewMode">
                        The Following Agents are using the Company Default Outbound Line but do not have access to it:
                    </span>
                    <span v-else>
                        The Following Agents will not have access to the line "{{ selectedLineName }}" after selecting it as the new Default Outbound Line:
                    </span>
                </div>
                <el-table :data="tableData" size="small" max-height="300" class="conflicts-table">
                    <el-table-column prop="first_name" label="Name" width="180">
                        <template v-slot="scope">
                            <span>{{ scope.row.first_name }} {{ scope.row.last_name }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="email" label="Email" />
                </el-table>
            </el-card>

            <el-card v-else-if="isPreviewMode" shadow="never" class="success-card mb-3 font-weight-bold rounded-card">
                No conflicts detected after selecting "{{ selectedLineName }}" as the new Default Outbound Line.
            </el-card>

            <div class="mb-3">
                To fix it, you can either:
            </div>

            <el-card
                shadow="never"
                class="rounded-card layout-row cursor-pointer mb-3"
                :class="{
                    'active-card': selectedCard === 'choose-new-outbound-line',
                    'disabled-card': loading
                }"
                @click.native="selectCard('choose-new-outbound-line')"
            >
                <div class="flex-icon">
                    <img
                        alt="Choose a new Outbound Line"
                        src="/assets/images/app-icons/icon-settings-blue.svg"
                        width="44"
                    />
                </div>
                <div class="flex-content ml-3">
                    <div class="font-weight-bold">
                        Choose a new Outbound Line
                    </div>
                    <div>
                        Select a different line. This will update the default outbound line for your company.
                    </div>
                    <campaign-selector
                        v-if="selectedCard === 'choose-new-outbound-line'"
                        v-model="selectedCampaignId"
                        class_prop="no-select mt-3 w-full"
                        placeholder="No Line Selected"
                        :show_paused="false"
                        :show-personal-lines="false"
                        filterable
                        :disabled="loading"
                        @change="previewConflictsWithNewOutboundLine"
                    />
                </div>
            </el-card>

            <el-card 
                shadow="never"
                class="rounded-card layout-row mb-3"
                :class="{
                    'active-card': selectedCard === 'add-agents-to-ring-group',
                    'cursor-pointer': !disableAddAgentsToRingGroupCard,
                    'cursor-not-allowed': disableAddAgentsToRingGroupCard,
                    'disabled-card': disableAddAgentsToRingGroupCard || loading
                }"
                @click.native="!disableAddAgentsToRingGroupCard && selectCard('add-agents-to-ring-group')"
            >
                <div class="flex-icon">
                    <img
                        alt="Add Agents to Line's Inbox"
                        src="/assets/images/app-icons/icon-add-users-green.svg"
                        width="44"
                    />
                </div>
                <div class="flex-content ml-3">
                    <div class="font-weight-bold">
                        Add Agents to Line's Inbox
                    </div>
                    <div>
                        Grant the affected agents access to the current line by
                        adding them as connected users to the {{ !defaultLineIsConnectedToPersonalInbox ? `"${defaultOutboundLineRingGroupName}"` : 'Line\'s Inbox' }}.
                    </div>
                    <div v-if="defaultLineIsConnectedToPersonalInbox" class="text-danger">
                        The Default Outbound Line is connected to a Personal Inbox. You can't add agents to it.
                    </div>
                </div>
            </el-card>

            <el-card 
                shadow="never"
                class="rounded-card layout-row cursor-pointer mb-3"
                :class="{
                    'active-card': selectedCard === 'dont-force-outbound-line',
                    'disabled-card': loading
                }"
                @click.native="selectCard('dont-force-outbound-line')"
            >
                <div class="flex-icon">
                    <img
                        alt="Don't Force an Outbound Line"
                        src="/assets/images/app-icons/icon-forbidden-red.svg"
                        width="44"
                    />
                </div>
                <div class="flex-content ml-3">
                    <div class="font-weight-bold">
                        Don't Force an Outbound Line
                    </div>
                    <div>
                        Remove the forced default outbound line restriction. Agents will be able to choose 
                        their own outbound line when making calls.
                    </div>
                </div>
            </el-card>

            <div class="text-right mt-4">
                <el-button
                    type="default"
                    @click="onClose"
                >
                    Cancel
                </el-button>
                <el-button 
                    type="primary"
                    :disabled="disableSaveChangesButton"
                    @click="saveChanges"
                >
                    Save Changes
                    <i class="el-icon-check el-icon-right"></i>
                </el-button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { mapState } from 'vuex'
import { RING_GROUP_BULK_ADD_USERS_TYPE_CONNECTED } from '../../constants/ring-group-bulk-add-users-types';

export default {
    name: 'UsersWithLineConflictsModal',

    props: {
        visible: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            selectedCampaignId: null,
            loading: false,
            selectedCard: null,
            userWithLineConflictsPreview: null,
        }
    },

    computed: {
        ...mapState('cache', ['current_company']),
        ...mapState(['campaigns', 'users', 'usersWithLineConflicts', 'ring_groups']),

        hasConflicts() {
            return this.usersWithLineConflicts.length > 0
        },

        isPreviewMode() {
            return this.selectedCampaignId &&
                Array.isArray(this.userWithLineConflictsPreview) &&
                this.selectedCampaignId !== this.current_company.default_outbound_campaign_id
        },

        tableData() {
            return Array.isArray(this.userWithLineConflictsPreview) 
                ? this.userWithLineConflictsPreview 
                : this.usersWithLineConflicts
        },

        disableSaveChangesButton() {
            if (this.loading || !this.selectedCard) {
                return true
            }

            if (this.selectedCard === 'choose-new-outbound-line') {
                return !this.selectedCampaignId
            }

            if (this.selectedCard === 'add-agents-to-ring-group') {
                return !this.lineIsConnectedToRingGroup || this.defaultLineIsConnectedToPersonalInbox
            }

            return false
        },

        disableAddAgentsToRingGroupButton() {
            return this.loading || !this.hasConflicts || !this.lineIsConnectedToRingGroup
        },

        companyDefaultOutboundLine() {
            return this.campaigns.find(campaign => campaign.id === this.current_company.default_outbound_campaign_id)
        },

        companyDefaultOutboundLineName() {
            return this.companyDefaultOutboundLine?.name || 'Unknown';
        },

        lineIsConnectedToRingGroup() {
            return !!this.companyDefaultOutboundLine?.ring_group_id
        },

        defaultLineIsConnectedToPersonalInbox() {
            return this.campaigns.find(campaign => campaign.id === this.current_company.default_outbound_campaign_id)?.user_id
        },

        defaultOutboundLineRingGroupName() {
            return this.lineIsConnectedToRingGroup
                ? this.ring_groups.find(ring_group => ring_group.id === this.companyDefaultOutboundLine.ring_group_id)?.name
                : '';
        },

        disableAddAgentsToRingGroupCard() {
            return !this.lineIsConnectedToRingGroup || this.defaultLineIsConnectedToPersonalInbox
        },

        selectedLineName() {
            return this.campaigns.find(campaign => campaign.id === this.selectedCampaignId)?.name || 'Unknown';
        },

        selectedLineIsConnectedToPersonalInbox() {
            return this.campaigns.find(campaign => campaign.id === this.selectedCampaignId)?.user_id
        },
    },

    methods: {
        selectCard(card) {
            this.selectedCard = card
        },

        onClose() {
            this.$emit('close')
            VueEvent.fire('show_users_with_line_conflicts_modal', false)
        },

        async previewConflictsWithNewOutboundLine(campaignId) {
            this.selectedCampaignId = campaignId

            if (this.selectedLineIsConnectedToPersonalInbox) {
                // Handler for when the default outbound campaign is connected to a personal inbox
                this.selectedCampaignId = null
            }

            if (campaignId === this.current_company.default_outbound_campaign_id) {
                this.userWithLineConflictsPreview = null
                return
            }

            try {
                this.loading = true

                const { data } = await axios.get('/api/v3/team-inbox/campaigns/conflicts', {
                    params: {
                        campaign_id: campaignId
                    }
                })

                // update the conflicts preview data after selecting a new line
                this.userWithLineConflictsPreview = data
            } catch (error) {
                console.log(error)
                this.$root.handleErrors(error.response)
            } finally {
                this.loading = false
            }
        },

        addAgentsToLineRingGroup() {
            const ringGroupName = this.defaultOutboundLineRingGroupName
            const usersWithLineConflictsLength = this.usersWithLineConflicts.length
            const pluralizedAgentsText = usersWithLineConflictsLength > 1 
                ? 'agents as connected users' 
                : 'agent as connected user'

            this.$confirm(
                `Are you sure you want to add ${usersWithLineConflictsLength} ${pluralizedAgentsText} to the Inbox "${ringGroupName}"?`,
                'Add Agents to Inbox',
                {
                    confirmButtonText: usersWithLineConflictsLength > 1 ? 'Yes, Add Agents' : 'Yes, Add Agent',
                    cancelButtonText: 'No, Cancel',
                    type: 'warning',
                    customClass: 'width-500 fixed'
                }
            ).then(async () => {
                this.loading = true

                try {
                    const { data } = await axios.put('/api/v1/ring-groups/bulk-add-users', {
                        ring_group_ids: [this.companyDefaultOutboundLine.ring_group_id],
                        user_ids: this.usersWithLineConflicts.map(user => user.id),
                        type: RING_GROUP_BULK_ADD_USERS_TYPE_CONNECTED
                    })

                    this.onSuccess(
                        'Add Connected Users to Inbox',
                        data.message
                    )
                } catch (error) {
                    console.log(error)
                    this.$root.handleErrors(error.response)
                } finally{
                    this.loading = false
                }
            }).catch(() => {
                // Cancel action handler
            })
        },

        async updateDefaultOutboundCampaign() {
            let message = `Are you sure you want to update the Default Outbound Line to "${this.selectedLineName}"?`
            const remainingConflictsCount = this.tableData.length

            if (remainingConflictsCount) {
                const agentsText = `${remainingConflictsCount} ${remainingConflictsCount > 1 ? 'agents' : 'agent'}`
                message = `
                    ${message}
                    <br>
                    <div class="mt-2 text-danger">
                        Warning: ${agentsText} will not be able access the selected line.
                    </div>`
            }

            this.$confirm(
                message,
                'Update Default Outbound Line',
                {
                    confirmButtonText: 'Update Default Outbound Line',
                    cancelButtonText: 'Cancel',
                    type: 'warning',
                    customClass: 'width-500 fixed',
                    dangerouslyUseHTMLString: true
                }
            ).then(async () => {
                this.loading = true

                try {
                    const { data } = await axios.patch('/api/v1/company/default-outbound-campaign', {
                        campaign_id: this.selectedCampaignId
                    })

                    this.onSuccess(
                        'Update Default Outbound Campaign',
                        data.message
                    )
                } catch (error) {
                    console.log(error)
                    this.$root.handleErrors(error.response)
                } finally{
                    this.loading = false
                }
            })
            .catch(() => {
                // Cancel action handler
            })
        },

        removeForceOutboundLine() {
            this.$confirm(
                'Are you sure you want to Disable the Force Outbound Line? This action will allow the agents to select a different Default Outbound Line.', 
                'Disable Force Outbound Line', 
                {
                    confirmButtonText: 'Disable Force Outbound Line',
                    cancelButtonText: 'Cancel',
                    type: 'warning',
                    customClass: 'width-500 fixed'
                }
            ).then(async () => {
                await axios.patch('/api/v1/company/flag', {
                    flag_name: 'force_outbound_line',
                    enabled: false
                })

                this.onSuccess(
                    'Disable Force Outbound Line',
                    'Force Outbound Line disabled successfully.'
                )
            })
            .catch(() => {
                // Cancel action handler
            })
        },

        onSuccess(title, message) {
            this.$notify.success({
                title,
                message
            })

            this.onClose()
            this.$root.getUsersWithLineConflicts()
        },

        saveChanges() {
            const actions = {
                'choose-new-outbound-line': this.updateDefaultOutboundCampaign,
                'add-agents-to-ring-group': this.addAgentsToLineRingGroup,
                'dont-force-outbound-line': this.removeForceOutboundLine
            }

            return actions[this.selectedCard]()
        }
    },

    watch: {
        visible (value) {
            if (value) {
                this.selectedCard = null
                this.userWithLineConflictsPreview = null
                this.selectedCampaignId = this.current_company.default_outbound_campaign_id

                if (this.defaultLineIsConnectedToPersonalInbox) {
                    this.selectedCampaignId = null
                }
            }
        },

        selectedCard (value, oldValue) {
            if (this.selectedCampaignId === this.current_company.default_outbound_campaign_id) {
                return
            }

            if (oldValue) {
                // Reset the conflicts preview to the default outbound campaign
                this.previewConflictsWithNewOutboundLine(this.current_company.default_outbound_campaign_id)
            }
        },

        '$route.fullPath' () {
            this.onClose()
        }
    }
}
</script>

<style>
.users-with-line-conflicts-modal {
    max-width: 530px;
}

.rounded-card {
    border-radius: 10px;
}

.disabled-card {
    opacity: 0.8;
    cursor: not-allowed;
}

.layout-row {
    display: flex !important;
    gap: 10px;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: flex-start;
    transition: all 0.3s ease;
}

.layout-row:not(.active-card):hover {
    background-color: #f5f5f5;
}

.layout-row .el-card__body {
    display: flex !important;
}

.flex-icon {
    flex: 0 0 auto;
}

.flex-content {
    flex: 1 1 auto;
}

.active-card {
    box-shadow: 0 0 0 2px #007bff !important;
    background-color: #007bff0d;
}

.conflicts-card {
    border: 2px solid orange;
    background-color: #ffa50026;
}

.success-card {
    border: 2px solid #00BF50;
    background-color: #00bf501f;
}

.conflicts-table,
.conflicts-table th,
.conflicts-table tr {
    background: transparent;
}

.conflicts-table td .cell {
    font-weight: 700;
}
</style>

